import React from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilState, useResetRecoilState } from 'recoil'
import {
  Button,
  BUTTON_VARIANT_PRIMARY,
  Checkbox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  RadioButton,
  Text,
  TEXT_VARIANT_SMALL,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_SMALL_LIGHT,
  Flex
} from 'mailup-web-components'
import { modalNotificationMessage } from 'Pages/OverviewMessage/recoil/state/modal'

import {
  MESSAGE_STR_TYPE_CONFIRMATION,
  MESSAGE_STR_TYPE_WELCOME
} from 'Constants/index'
import { saveAsNotificationMessageCallback } from 'Pages/OverviewMessage/recoil/callbacks/callbacks'
import styled from '@emotion/styled'

const ModalNotificationMessage = () => {
  const intl = useIntl()

  const [
    {
      show,
      selectedMessageType,
      isEnableConfirmationRadio,
      isEnableWelcomeRadio,
      enableNotification
    },
    setModalNotificationMessage
  ] = useRecoilState(modalNotificationMessage)

  const closeModal = useResetRecoilState(modalNotificationMessage)

  const saveAsNotificationMessage = useRecoilCallback(
    saveAsNotificationMessageCallback
  )

  const handleClose = () => {
    closeModal()
  }

  const handleChangeMessageType = (e, messageType) => {
    e.preventDefault()
    if (
      messageType === MESSAGE_STR_TYPE_CONFIRMATION &&
      !isEnableConfirmationRadio
    ) {
      return
    }

    if (messageType === MESSAGE_STR_TYPE_WELCOME && !isEnableWelcomeRadio) {
      return
    }

    setModalNotificationMessage(prev => {
      return { ...prev, selectedMessageType: messageType }
    })
  }

  const handleChangeSetActive = e => {
    setModalNotificationMessage(prev => {
      return { ...prev, enableNotification: e.target.checked }
    })
  }

  const handleSave = () => {
    saveAsNotificationMessage(selectedMessageType, enableNotification)
  }

  const Label = () => (
    <>
      <Text variant={TEXT_VARIANT_BOLD} htmlElement='span'>
        {intl.formatMessage({
          id: 'Load.SavedEmailWizard.ConfirmationMessage'
        })}
      </Text>
      <Text variant={TEXT_VARIANT_SMALL} htmlElement='span'>
        {' ('}
        {intl.formatMessage({
          id: 'Load.MessageOverview.MustContainSubscriptionLink'
        })}
        {')'}
      </Text>
    </>
  )

  return (
    <Modal show={show} showClose closeOnOverlayClick onClose={handleClose}>
      <ModalHead
        title={intl.formatMessage({
          id: 'Load.SavedEmailWizard.ModalSetAsNotificationPageTitle'
        })}
      />
      <ModalBody>
        <Flex flexDirection='column' rowGap='24px'>
          <StyledRadioContainer
            disabled={!isEnableConfirmationRadio}
            onClick={e =>
              handleChangeMessageType(e, MESSAGE_STR_TYPE_CONFIRMATION)
            }
          >
            <RadioButton
              value={MESSAGE_STR_TYPE_CONFIRMATION}
              checked={selectedMessageType === MESSAGE_STR_TYPE_CONFIRMATION}
              label={<Label />}
              onChange={() => {}}
              disabled={!isEnableConfirmationRadio}
              data-mu-test='option-confirmation'
            />
            <Flex padding='0 0 0 26px'>
              <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                {intl.formatMessage({
                  id: 'Load.MessageOverview.MustContainSubscriptionLinkDescr'
                })}
              </Text>
            </Flex>
          </StyledRadioContainer>

          <StyledRadioContainer
            disabled={!isEnableWelcomeRadio}
            onClick={e => handleChangeMessageType(e, MESSAGE_STR_TYPE_WELCOME)}
          >
            <RadioButton
              value={MESSAGE_STR_TYPE_WELCOME}
              checked={selectedMessageType === MESSAGE_STR_TYPE_WELCOME}
              label={intl.formatMessage({
                id: 'Load.SavedEmailWizard.WelcomeMessage'
              })}
              onChange={() => {}}
              disabled={!isEnableWelcomeRadio}
              data-mu-test='option-welcome'
            />
            <Flex padding='0 0 0 26px'>
              <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
                {intl.formatMessage({
                  id: 'Load.MessageOverview.WelcomeMessageDescr'
                })}
              </Text>
            </Flex>
          </StyledRadioContainer>
        </Flex>
      </ModalBody>

      <ModalFoot>
        <StyledLeftButton>
          <Flex
            height='34px'
            padding='8px 16px'
            alignItems='center'
            justifyContent='center'
          >
            <Checkbox
              label={intl.formatMessage({
                id: 'Load.EmailMessageList.PutAsDefault'
              })}
              checked={enableNotification}
              onChange={handleChangeSetActive}
            />
          </Flex>
        </StyledLeftButton>
        <span />
        <StyledRightButton
          onClick={handleSave}
          kind={BUTTON_VARIANT_PRIMARY}
          disabled={!selectedMessageType}
        >
          {intl.formatMessage({
            id: 'Load.EmailMessageList.SetAsNotificationPage'
          })}
        </StyledRightButton>
      </ModalFoot>
    </Modal>
  )
}

export default ModalNotificationMessage

const StyledRadioContainer = styled.div`
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? '0.4' : '1')};
`

const StyledLeftButton = styled.span`
  line-height: 1em;
  border-radius: 4px;
  text-transform: uppercase;
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;

  font-size: 13px;
  color: var(--blue-chill);
  border: 1px solid var(--java);
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  float: right;

  :hover {
    text-decoration: none;
    cursor: pointer;
    color: var(--blue-chill) !important;
    background-color: transparent;
    border-color: var(--blue-chill);
    outline: 0;
  }

  .c-checkbox__label {
    &::before {
      height: 14px;
      width: 14px;
      vertical-align: bottom;
    }
  }
`

const StyledRightButton = styled(Button)`
  border-radius: 0 4px 4px 0 !important;
  border-left: 0 !important;
  margin-left: 0px !important;
  margin-right: 0px !important;
  > span {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`
